import dayjs from 'dayjs';
import React, { useRef } from 'react';

/** A calendar day, `YYYY-MM-DD`. Plain strings compare in date order and carry no timezone. */
export type CalendarDay = string;

interface MonthGridProps {
  /** `YYYY-MM`. */
  month: string;
  /** Today in the restaurant's timezone. */
  today: CalendarDay;
  locale: string;
  /** BEM block for the class names — lets RangeCalendar keep its existing styles. */
  block?: string;
  dayModifiers?: (day: CalendarDay) => string[];
  dayPressed?: (day: CalendarDay) => boolean | undefined;
  renderDay?: (day: CalendarDay) => React.ReactNode;
  onDayClick?: (day: CalendarDay) => void;
  /** Optional label to append to the button's aria-label. */
  dayLabel?: (day: CalendarDay) => string | undefined;
}

const MOVES: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

/**
 * One Monday-first month of day buttons. Shared by the dashboard's RangeCalendar and the
 * timesheet calendar's sidebar. Days are `YYYY-MM-DD` strings throughout; callers convert to
 * and from instants in the restaurant's timezone at the edges, so nothing here can shift a day.
 */
const MonthGrid = ({
  month,
  today,
  locale,
  block = 'month-grid',
  dayModifiers,
  dayPressed,
  renderDay,
  onDayClick,
  dayLabel,
}: MonthGridProps) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const first = dayjs(`${month}-01`);
  const leading = (first.day() + 6) % 7;
  const days = first.daysInMonth();
  const trailing = (7 - ((leading + days) % 7)) % 7;
  const dayName = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const fullDate = new Intl.DateTimeFormat(locale, { dateStyle: 'full' });
  // 2024-01-01 was a Monday.
  const weekdays = Array.from({ length: 7 }, (_, index) => dayName.format(new Date(2024, 0, 1 + index)));

  const handleDayKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const move = MOVES[event.key];
    const current = event.currentTarget.dataset.day;
    if (move === undefined || !current) return;
    const target = dayjs(current).add(move, 'day').format('YYYY-MM-DD');
    const button = gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${target}"]`);
    if (button) {
      event.preventDefault();
      button.focus();
    }
  };

  return (
    <div className={`${block}__grid`} ref={gridRef}>
      {weekdays.map((weekday) => (
        <div className={`${block}__weekday`} key={weekday} aria-hidden>
          {weekday}
        </div>
      ))}
      {Array.from({ length: leading }, (_, index) => (
        <span className={`${block}__day ${block}__day--outside`} key={`lead-${index}`} aria-hidden>
          {first.subtract(leading - index, 'day').date()}
        </span>
      ))}
      {Array.from({ length: days }, (_, index) => {
        const date = first.add(index, 'day');
        const value = date.format('YYYY-MM-DD');
        const classes = [
          `${block}__day`,
          value === today && `${block}__day--today`,
          ...(dayModifiers?.(value) ?? []).map((modifier) => `${block}__day--${modifier}`),
        ]
          .filter(Boolean)
          .join(' ');
        const label = dayLabel?.(value);
        const ariaLabel = label ? `${fullDate.format(date.toDate())} — ${label}` : fullDate.format(date.toDate());
        return (
          <button
            type="button"
            key={value}
            data-day={value}
            className={classes}
            aria-label={ariaLabel}
            aria-pressed={dayPressed?.(value)}
            aria-current={value === today ? 'date' : undefined}
            onClick={() => onDayClick?.(value)}
            onKeyDown={handleDayKeyDown}
          >
            <span className={`${block}__number`}>{date.date()}</span>
            {renderDay?.(value)}
          </button>
        );
      })}
      {Array.from({ length: trailing }, (_, index) => (
        <span className={`${block}__day ${block}__day--outside`} key={`trail-${index}`} aria-hidden>
          {index + 1}
        </span>
      ))}
    </div>
  );
};

export default MonthGrid;
